class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('details > .search-modal button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.querySelector('details > .search-modal button[type="reset"]').addEventListener(
      'click',
      this.reset.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
    this.holdsStickyPark = false;
    this.savedScrollY = null;
    this.scrollLockTimer = null;
    this.viewportOffset = 0;
  }

  disconnectedCallback() {
    this.releaseScrollLock();
    if (!this.holdsStickyPark) return;
    this.holdsStickyPark = false;
    restoreStickyHeaderAfterDrawer();
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  // Predictive search sets `open` on itself for exactly as long as it is showing
  // results, and clears it again when they go away, so it is the honest signal for
  // "there is something here to read".
  hasResults() {
    return !!this.querySelector('predictive-search[open]');
  }

  // Anything inside the modal that is not the field or the results is veil: the
  // empty blurred area around them. Deliberately not keyed on .modal-overlay --
  // that element's hit area comes from an ::after at top: 100%, which sits outside
  // the visible modal, so tapping the veil never actually reached it.
  isVeil(target) {
    if (!this.contains(target)) return false;

    // Everything in here stretches to fill the modal: the <predictive-search>
    // element wraps the whole column, and .search-modal__form is height: 100%. So
    // the empty area the visitor perceives as veil is literally inside the form,
    // and excluding either of those wrappers is why tapping it never dismissed
    // anything. Only the real controls count as content — the input pill, the
    // buttons, and the results panel (div.predictive-search, not the element of
    // the same name).
    if (target.closest('.field, .search__button, .predictive-search, .search__recommendation')) return false;

    return !!target.closest('.search-modal');
  }

  onBodyClick(event) {
    const onVeil = this.isVeil(event.target);

    // Tapping the veil dismisses search while there is nothing to read. Once
    // results are up the visitor is scrolling them, and a touch that starts on the
    // veil is far more likely to be the beginning of that scroll than a deliberate
    // dismiss — closing there would throw away the results they just typed for.
    // The close button and Escape still work in both states.
    if (onVeil && this.hasResults()) return;

    if (!this.contains(event.target) || onVeil) this.close();
  }

  // iOS Safari does not move position:fixed elements when the on-screen keyboard
  // opens — it scrolls the layout viewport instead. A top-anchored fixed overlay
  // therefore ends up above the visible area, taking the field being typed into
  // with it, which is exactly the "can't see what I'm typing" report.
  // visualViewport.offsetTop is how far the visible region has moved; handing it to
  // CSS lets the content shift back down by the same amount.
  syncViewportOffset() {
    const vv = window.visualViewport;
    if (!vv) return;
    // Rounded so sub-pixel churn during a keyboard animation does not thrash layout.
    this.setViewportOffset(Math.max(0, Math.round(vv.offsetTop)));
  }

  // Writes only on a change, and leaves 0 to the CSS fallback, so opening and
  // closing without a keyboard touch no inline style. An inline-style write in
  // the header makes Chrome re-check the page's :has() rules, because one of
  // them tests [style] (vendor-shopify-privacy.css).
  setViewportOffset(px) {
    if (px === this.viewportOffset) return;
    this.viewportOffset = px;
    if (px) {
      this.style.setProperty('--search-vv-offset', `${px}px`);
    } else {
      this.style.removeProperty('--search-vv-offset');
    }
  }

  pinSavedScroll() {
    if (!Number.isFinite(this.savedScrollY)) return;
    const current = window.pageYOffset || document.documentElement.scrollTop;
    if (Math.abs(current - this.savedScrollY) < 1) return;
    window.scrollTo(0, this.savedScrollY);
  }

  releaseScrollLock() {
    clearTimeout(this.scrollLockTimer);
    this.scrollLockTimer = null;
    document.body.classList.remove('search-modal--closing');
  }

  open() {
    this.savedScrollY = window.pageYOffset || document.documentElement.scrollTop;
    setScrollbarWidth();
    setHeaderBottomPosition();

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.onViewportChangeEvent = this.onViewportChangeEvent || this.syncViewportOffset.bind(this);

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    // Reopened during the close fade-out: --open takes the scroll lock over from
    // --closing in the same frame, so it never drops in between.
    this.releaseScrollLock();
    document.body.classList.add('search-modal--open');
    if (!this.holdsStickyPark) {
      this.holdsStickyPark = true;
      parkStickyHeaderForDrawer();
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportChangeEvent);
      window.visualViewport.addEventListener('scroll', this.onViewportChangeEvent);
    }
    this.syncViewportOffset();

    trapFocus(
      this.querySelector('details > .search-modal [tabindex="-1"]'),
      this.querySelector('details > .search-modal input:not([type="hidden"])')
    );
    this.pinSavedScroll();
  }

  close() {
    removeTrapFocus();
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    // The scroll lock outlives the overlay by the veil's fade-out (250 ms, i.e.
    // --duration-default): search-modal--closing keeps overflow: hidden until
    // then. Released in the tap's own frame, it made Chrome repaint the whole
    // document in that frame (one 412x5952 Paint of 46-150 ms at 4x CPU on a
    // collection page). The filter drawers hold theirs through --closing too.
    // replace() is false when search was not open, so a stray close() locks
    // nothing.
    if (document.body.classList.replace('search-modal--open', 'search-modal--closing')) {
      clearTimeout(this.scrollLockTimer);
      this.scrollLockTimer = setTimeout(() => this.releaseScrollLock(), 250);
    }
    if (this.holdsStickyPark) {
      this.holdsStickyPark = false;
      restoreStickyHeaderAfterDrawer();
    }
    this.pinSavedScroll();
    requestAnimationFrame(() => this.pinSavedScroll());

    if (window.visualViewport && this.onViewportChangeEvent) {
      window.visualViewport.removeEventListener('resize', this.onViewportChangeEvent);
      window.visualViewport.removeEventListener('scroll', this.onViewportChangeEvent);
    }
    this.setViewportOffset(0);

    document.dispatchEvent(new CustomEvent('searchmodal:close'));
  }

  reset(event) {
    event.preventDefault();
    this.querySelector('input[type="search"]').value = '';
  }
}

customElements.define('search-modal', SearchModal);
